import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { activeLanguage, formatDate, formatDateTime } from "@/lib/i18n/format";
import type { ApprovalDecision, ApprovalItem, ChatApproval, WritePreviewChange } from "@/lib/types/chat";
import {
  MAX_VISIBLE_ITEMS,
  actionLabelKey,
  customFieldName,
  dateValue,
  entityLabelKey,
  fieldLabelKey,
  formatValue,
  hasBefore,
  isOpaque,
  splitTool,
  valueLabelKey,
} from "@/lib/utils/approval-card";

interface AskCoffeeApprovalCardProps {
  approval: ChatApproval;
  onDecide: (decision: ApprovalDecision) => void;
}

const STATUS_KEYS = {
  approved: "askCoffee.approvalStatusApproved",
  declined: "askCoffee.approvalStatusDeclined",
  expired: "askCoffee.approvalStatusExpired",
  superseded: "askCoffee.approvalStatusSuperseded",
} as const;

/**
 * The changes a paused Ask Coffee turn wants to make, shown before anything is written.
 * Everything here comes from the MCP server's dry run, never from the model's prose:
 * approving runs exactly these calls.
 */
export default function AskCoffeeApprovalCard({ approval, onDecide }: AskCoffeeApprovalCardProps) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const { card, status } = approval;
  const items = showAll ? card.items : card.items.slice(0, MAX_VISIBLE_ITEMS);
  const hidden = card.items.length - items.length;

  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2.5 text-sm" data-testid="approval-card">
      <div className="flex items-center gap-1.5 font-medium text-foreground">
        <ShieldCheck className="h-3.5 w-3.5 text-accent" />
        {t("askCoffee.approvalTitle")}
      </div>

      <ul className="mt-2 space-y-2">
        {items.map((item) => (
          <ApprovalRow key={item.tool_call_id} item={item} />
        ))}
      </ul>

      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-1.5 text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
        >
          {t("askCoffee.approvalShowAll", { count: card.items.length })}
        </button>
      )}

      <div className="mt-3 flex items-center justify-end gap-2">
        {status === "pending" ? (
          <>
            <Button size="sm" variant="outline" onClick={() => onDecide("declined")}>
              {t("askCoffee.approvalDecline")}
            </Button>
            <Button size="sm" onClick={() => onDecide("approved")}>
              {t("askCoffee.approvalApprove")}
            </Button>
          </>
        ) : (
          <span className={cn("text-xs", status === "approved" ? "text-foreground" : "text-muted-foreground")}>
            {t(STATUS_KEYS[status])}
          </span>
        )}
      </div>
    </div>
  );
}

function ApprovalRow({ item }: { item: ApprovalItem }) {
  const { t } = useTranslation();
  const { action, entity } = splitTool(item.tool);
  const actionKey = actionLabelKey(action);
  const entityKey = entityLabelKey(entity);
  const { preview } = item;
  const isDelete = action === "delete" || action === "remove";

  return (
    <li className={cn("rounded-md border px-2.5 py-2", isDelete ? "border-destructive/40 bg-destructive/5" : "border-border bg-background")}>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant={isDelete ? "destructive" : "secondary"} className="text-[10px] px-1.5 py-0">
          {actionKey ? t(actionKey) : item.tool}
        </Badge>
        {entityKey && <span className="text-xs text-muted-foreground">{t(entityKey)}</span>}
        {preview.label && <span className="text-xs font-semibold text-foreground truncate">{preview.label}</span>}
      </div>

      {preview.changes.length > 0 && (
        <dl className="mt-1.5 space-y-0.5">
          {preview.changes.map((change) => (
            <ChangeLine key={change.field} entity={entity} change={change} />
          ))}
        </dl>
      )}

      {preview.warnings && preview.warnings.length > 0 && (
        <p className="mt-1 text-[11px] text-muted-foreground">{t("askCoffee.approvalCurrentUnknown")}</p>
      )}
    </li>
  );
}

function ChangeLine({ entity, change }: { entity: string; change: WritePreviewChange }) {
  const { t } = useTranslation();
  const labelKey = fieldLabelKey(entity, change.field);
  const label = labelKey ? t(labelKey) : customFieldName(change.field);
  const empty = t("askCoffee.approvalEmpty");
  // Enum values and dates are shown in the reader's language; everything else as stored.
  const show = (value: unknown): string => {
    const key = valueLabelKey(entity, change.field, value);
    if (key) return t(key);
    const asDate = dateValue(change.field, value);
    if (asDate) {
      return asDate.withTime
        ? formatDateTime(asDate.date, activeLanguage())
        : formatDate(asDate.date, activeLanguage());
    }
    return formatValue(value) ?? empty;
  };
  const after = show(change.after);

  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 text-xs">
      <dt className="text-muted-foreground">{label}:</dt>
      <dd className="flex flex-wrap items-baseline gap-x-1.5 text-foreground">
        {isOpaque(change) ? (
          <span>{t("askCoffee.approvalContentChanged")}</span>
        ) : (
          <>
            {hasBefore(change) && (
              <>
                <span className="text-muted-foreground line-through">{show(change.before)}</span>
                <ArrowRight className="h-3 w-3 self-center text-muted-foreground" />
              </>
            )}
            <span className="font-medium break-words whitespace-pre-line">{after}</span>
          </>
        )}
      </dd>
    </div>
  );
}
